/**
 * usePropagateEntity — encapsulates all propagation + undo + banner-timer logic
 * that previously lived inline in TransactionDetail.
 *
 * Responsibilities:
 *  1. Write a new alias + category rule to the learning bag (snapshotting the
 *     entity's PRE-EXISTING aliases first so undo can restore exactly).
 *  2. Fetch uncategorized transactions, compute matches, bulk-link them.
 *  3. Expose the result + 8s auto-dismiss timer.
 *  4. On undo: restore the alias snapshot, revert matched rows (individually,
 *     tolerating partial failures via Promise.allSettled), and surface an error
 *     string if any rows couldn't be reverted.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useRef, useState } from "react"
import { api } from "@/lib/api"
import { deriveAliasPattern } from "@/lib/categorization-client"
import { buildResolveContextExtras, computePropagation } from "@/lib/propagate-entity"
import type { CategoryRule, LearningBag, MerchantAlias, PersonAlias } from "./use-learning"
import { useLearning, useUpdateLearning } from "./use-learning"
import { useMerchants } from "./use-merchants"
import { usePersons } from "./use-persons"
import { useUncategorizedTransactions } from "./use-uncategorized-transactions"

// ── Public types ────────────────────────────────────────────────────────────

export interface PropagateArgs {
  sourceTxnId: string
  sourceDescription: string
  entity: { type: "merchant"; merchantId: string } | { type: "person"; personId: string }
  categoryId: string | null
}

export interface PropagationResult {
  matchedIds: string[]
  withCategory: boolean
  /** true when the bulk-link call failed (single-row link already succeeded) */
  degraded: boolean
}

// Internal snapshot stored before we mutate the bag — needed for correct undo.
interface AliasSnapshot {
  entity: PropagateArgs["entity"]
  /** Merchant aliases that were present BEFORE the new alias was written. */
  prevMerchantAliases: MerchantAlias[]
  /** Person aliases that were present BEFORE the new alias was written. */
  prevPersonAliases: PersonAlias[]
  /** Category rule that was present BEFORE (null if absent). */
  prevRule: CategoryRule | null
}

interface UndoState {
  snapshot: AliasSnapshot
  result: PropagationResult
  categoryId: string | null
}

// ── Hook ────────────────────────────────────────────────────────────────────

export function usePropagateEntity() {
  const queryClient = useQueryClient()
  const { data: learningData } = useLearning()
  const updateLearning = useUpdateLearning()

  // Uncategorized transactions — shared paged fetch so propagation scans ALL
  // rows (not just the first 200). The hook keeps its own enabled state; we
  // always want it warm while this hook is mounted (i.e. the panel is open).
  const { rows: uncategorized } = useUncategorizedTransactions({ enabled: true })

  // Merchants + persons for fuzzball/alias fallback — mirrors what the
  // auto-categorize dialog does so propagation and the dialog resolve identically.
  const { data: merchantsResp } = useMerchants()
  const { data: personsResp } = usePersons()

  const ctxExtras = useMemo(
    () => buildResolveContextExtras(merchantsResp?.data ?? [], personsResp?.data ?? []),
    [merchantsResp, personsResp],
  )

  const [result, setResult] = useState<PropagationResult | null>(null)
  const [undoState, setUndoState] = useState<UndoState | null>(null)
  const [undoError, setUndoError] = useState<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 8-second auto-dismiss — guarded so it can't fire mid-undo.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the timer restarts only when result changes; isPending is read when it fires
  useEffect(() => {
    if (!result) return
    timerRef.current = setTimeout(() => {
      // Guard: don't dismiss while undo is in flight.
      if (undoMutation.isPending) return
      setResult(null)
      setUndoState(null)
    }, 8000)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
    // We intentionally exclude undoMutation.isPending from deps: we only set
    // up the timer when result changes, and the callback checks isPending at
    // fire time (closure-captured ref via the mutation object).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result])

  // ── Undo mutation ─────────────────────────────────────────────────────────

  const undoMutation = useMutation({
    onMutate: () => {
      // Cancel the auto-dismiss timer while undo is in flight.
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    },
    mutationFn: async (state: UndoState) => {
      const { snapshot, result: propagationResult } = state

      // Revert each propagated transaction using allSettled so partial failures
      // don't short-circuit the rest.
      const revertResults = await Promise.allSettled(
        propagationResult.matchedIds.map((id) =>
          api.patch(`/transactions/${id}`, {
            merchantId: null,
            personId: null,
            ...(propagationResult.withCategory ? { categoryId: null } : {}),
          }),
        ),
      )

      const failures = revertResults.filter((r) => r.status === "rejected")
      if (failures.length > 0) {
        const succeeded = propagationResult.matchedIds.length - failures.length
        throw new Error(
          `Reverted ${succeeded} of ${propagationResult.matchedIds.length} — try again`,
        )
      }

      // Restore the alias snapshot exactly (not blanket-remove).
      const { entity, prevMerchantAliases, prevPersonAliases, prevRule } = snapshot

      // The revert already succeeded: a learning-bag failure must not fail the Undo.
      try {
        await updateLearning.mutateAsync((bag) => {
          const next = structuredClone(bag)
          if (entity.type === "merchant") {
            // Restore merchant aliases to exactly what they were before.
            next.merchantAliases = [
              ...next.merchantAliases.filter((a) => a.merchantId !== entity.merchantId),
              ...prevMerchantAliases,
            ]
            // Restore or remove the rule.
            if (prevRule) {
              next.merchantRules[entity.merchantId] = prevRule
            } else {
              delete next.merchantRules[entity.merchantId]
            }
          } else {
            next.personAliases = [
              ...next.personAliases.filter((a) => a.personId !== entity.personId),
              ...prevPersonAliases,
            ]
            if (prevRule) {
              next.personRules[entity.personId] = prevRule
            } else {
              delete next.personRules[entity.personId]
            }
          }
          return next
        })
      } catch {
        // Best-effort alias restore.
      }
    },
    onSuccess: () => {
      setResult(null)
      setUndoState(null)
      setUndoError(null)
      queryClient.invalidateQueries({ queryKey: ["transactions"] })
      queryClient.invalidateQueries({ queryKey: ["dashboard"] })
    },
    onError: (err: Error) => {
      setUndoError(err.message)
      // Restart the dismiss timer after an undo failure so the banner doesn't
      // sit forever.
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        setResult(null)
        setUndoState(null)
        setUndoError(null)
      }, 8000)
    },
  })

  // ── propagate() ───────────────────────────────────────────────────────────

  /**
   * Returns `true` if at least one match was found and the banner will be shown.
   * The caller uses this to decide whether to keep the panel open.
   */
  async function propagate(args: PropagateArgs): Promise<boolean> {
    const { sourceTxnId, sourceDescription, entity, categoryId } = args

    // Read the current bag from the query cache (or fall back to fetched data).
    const emptyBag: LearningBag = {
      merchantAliases: [],
      personAliases: [],
      merchantRules: {},
      personRules: {},
    }
    const currentBag =
      queryClient.getQueryData<{ bag: LearningBag }>(["household-learning"])?.bag ??
      learningData?.bag ??
      emptyBag

    // ── Snapshot pre-existing aliases/rule BEFORE writing the new one ─────
    const entityId = entity.type === "merchant" ? entity.merchantId : entity.personId
    const snapshot: AliasSnapshot = {
      entity,
      prevMerchantAliases:
        entity.type === "merchant"
          ? currentBag.merchantAliases.filter((a) => a.merchantId === entityId)
          : [],
      prevPersonAliases:
        entity.type === "person"
          ? currentBag.personAliases.filter((a) => a.personId === entityId)
          : [],
      prevRule:
        entity.type === "merchant"
          ? (currentBag.merchantRules[entityId] ?? null)
          : (currentBag.personRules[entityId] ?? null),
    }

    // ── Write new alias + rule to the bag ─────────────────────────────────
    const pattern = deriveAliasPattern(sourceDescription)
    const ts = new Date().toISOString()

    try {
      await updateLearning.mutateAsync((bag) => {
        const next = structuredClone(bag)
        if (entity.type === "merchant") {
          next.merchantAliases = [
            ...next.merchantAliases.filter((a) => a.merchantId !== entity.merchantId),
            { pattern, merchantId: entity.merchantId, createdAt: ts },
          ]
          if (categoryId) {
            next.merchantRules[entity.merchantId] = {
              categoryId,
              lastUserCategoryAt: ts,
            }
          }
        } else {
          next.personAliases = [
            ...next.personAliases.filter((a) => a.personId !== entity.personId),
            { pattern, personId: entity.personId, createdAt: ts },
          ]
          if (categoryId) {
            next.personRules[entity.personId] = {
              categoryId,
              lastUserCategoryAt: ts,
            }
          }
        }
        return next
      })
    } catch {
      // Don't block on learning-bag failures — continue to propagation.
    }

    // ── Read the updated bag (post-write) for propagation context ─────────
    const updatedBag =
      queryClient.getQueryData<{ bag: LearningBag }>(["household-learning"])?.bag ??
      learningData?.bag ??
      emptyBag

    // ── Build unmatched list ──────────────────────────────────────────────
    const unmatched = uncategorized
      .filter((t) => !t.isTransfer && !t.isIgnored && t.id !== sourceTxnId)
      .map((t) => ({ id: t.id, description: t.description }))

    // ── computePropagation needs entity context (for fuzzball fallback) ───
    const { matchedIds } = computePropagation({
      source: { id: sourceTxnId, description: sourceDescription },
      unmatched,
      entity,
      ctxExtras,
      bag: updatedBag,
    })

    if (matchedIds.length === 0) {
      // Nothing to propagate — no result to show.
      return false
    }

    // ── Bulk-link matched rows ─────────────────────────────────────────────
    const links = matchedIds.map((id) => {
      const base =
        entity.type === "merchant"
          ? { transactionId: id, merchantId: entity.merchantId }
          : { transactionId: id, personId: entity.personId }
      return categoryId ? { ...base, categoryId } : base
    })

    let degraded = false
    try {
      await api.post("/transactions/bulk-link-entity", { links })
    } catch {
      // Bulk-link failed; the single-row link for the source already succeeded.
      // Degrade gracefully — show toast but with degraded flag.
      degraded = true
    }

    const propagationResult: PropagationResult = {
      matchedIds: degraded ? [] : matchedIds,
      withCategory: categoryId != null,
      degraded,
    }

    setResult(propagationResult)
    setUndoState({ snapshot, result: propagationResult, categoryId })
    setUndoError(null)
    // Return true so the caller knows a banner will be shown — keep panel open.
    return propagationResult.matchedIds.length > 0
  }

  // ── dismiss() ─────────────────────────────────────────────────────────────

  function dismiss() {
    if (timerRef.current) clearTimeout(timerRef.current)
    setResult(null)
    setUndoState(null)
    setUndoError(null)
  }

  // ── undo() ────────────────────────────────────────────────────────────────

  function undo() {
    if (!undoState) return
    undoMutation.mutate(undoState)
  }

  return {
    propagate,
    undo,
    dismiss,
    result,
    isUndoing: undoMutation.isPending,
    undoError,
  }
}
